import type { LeaveTypeDTO } from "@/modules/hr/domain/contracts";
import { HR_LEAVE_ATTACHMENT_KEY_PREFIX } from "@/modules/hr/domain/leave-attachment";
import { PresignedFileDropZone } from "@/shared/ui/PresignedFileDropZone";
import {
  Button,
  Card,
  CardContent,
  FormInput,
  FormRoot,
  FormTextarea,
  RadioGroup,
  RadioGroupItem,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { Briefcase, Calendar } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import type { LeaveRequestCreateFormSample } from "./leaveRequestCreateFormSamples";

const LeaveRequestFormSchema = z
  .object({
    leaveTypeId: z.string().min(1, "ກະລຸນາເລືອກປະເພດການລາ"),
    startDate: z.string().min(1, "ວັນທີເລີ່ມຕົ້ນຕ້ອງກຳນົດ"),
    endDate: z.string().min(1, "ວັນທີສິ້ນສຸດຕ້ອງກຳນົດ"),
    reason: z.string().optional(),
    attachmentKey: z.string().optional().nullable(),
  })
  .refine(
    (data) =>
      !data.startDate ||
      !data.endDate ||
      new Date(data.endDate) >= new Date(data.startDate),
    { message: "ວັນທີສິ້ນສຸດຕ້ອງບໍ່ກ່ອນວັນທີເລີ່ມຕົ້ນ", path: ["endDate"] },
  );

export type LeaveRequestFormValues = z.infer<typeof LeaveRequestFormSchema>;

type LeaveRequestFormProps = {
  leaveTypes: LeaveTypeDTO[];
  initialValues?: Partial<LeaveRequestFormValues>;
  onSubmit: (values: LeaveRequestFormValues) => void;
  submitting?: boolean;
  submitLabel?: string;
  cancelTo?: string;
  /** When enabled, keeps submit/cancel buttons pinned to the bottom while scrolling. */
  stickySubmit?: boolean;
  /** Enables an "auto fill sample" button on create pages. */
  autoFillGetSamples?: () => LeaveRequestCreateFormSample[];
  autoFillLabel?: string;
};

export function LeaveRequestForm({
  leaveTypes,
  initialValues,
  onSubmit,
  submitting,
  submitLabel = "ສົ່ງຄຳຮ້ອງ",
  cancelTo,
  stickySubmit = false,
  autoFillGetSamples,
  autoFillLabel = "ເພີ່ມຕົວຢ່າງ",
}: LeaveRequestFormProps) {
  const methods = RHF.useForm<LeaveRequestFormValues>({
    resolver: zodResolver(LeaveRequestFormSchema),
    defaultValues: {
      leaveTypeId: "",
      startDate: "",
      endDate: "",
      reason: "",
      attachmentKey: null,
      ...initialValues,
    },
  });

  const [sampleIndex, setSampleIndex] = useState(0);

  const handleAutoFillSample = () => {
    if (!autoFillGetSamples) return;
    const samples = autoFillGetSamples();
    if (!samples || samples.length === 0) return;
    const s = samples[sampleIndex % samples.length];
    if (!s) return;

    methods.setValue("startDate", s.startDate, {
      shouldDirty: true,
      shouldValidate: true,
    });
    methods.setValue("endDate", s.endDate, {
      shouldDirty: true,
      shouldValidate: true,
    });
    methods.setValue("reason", s.reason, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setSampleIndex((prev) => (prev + 1) % samples.length);
    toast.success("ເພີ່ມຕົວຢ່າງແລ້ວ");
  };

  return (
    <FormRoot<LeaveRequestFormValues>
      methods={methods}
      onSubmit={onSubmit}
      className="space-y-6"
    >
      <div>
        <p className="mb-3 block font-medium text-sm">ປະເພດການລາ</p>
        <RHF.Controller
          name="leaveTypeId"
          render={({ field }) => (
            <RadioGroup
              data-tourid="hr-leave-type-selector"
              value={field.value}
              onValueChange={field.onChange}
              className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
            >
              {leaveTypes.map((t) => {
                const isSelected = field.value === String(t.id);
                return (
                  <label
                    key={t.id}
                    htmlFor={`leave-type-${t.id}`}
                    className="cursor-pointer"
                  >
                    <RadioGroupItem
                      id={`leave-type-${t.id}`}
                      value={String(t.id)}
                      className="sr-only"
                    />
                    <Card
                      className={`h-full p-4 transition-colors ${
                        isSelected
                          ? "border-2 border-black bg-black text-white shadow-md"
                          : "border-2 border-border hover:border-black/50 hover:bg-muted/30"
                      }`}
                    >
                      <CardContent className="p-2 py-0">
                        <div className="mb-2 flex items-start gap-3">
                          <span
                            aria-hidden="true"
                            className={`mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg ${
                              isSelected ? "bg-white/20" : "bg-muted"
                            }`}
                          >
                            {t.code === "SICK" || t.name.includes("ເຈັບ") ? (
                              <Briefcase
                                className={
                                  isSelected
                                    ? "text-white"
                                    : "text-muted-foreground"
                                }
                                size={20}
                              />
                            ) : (
                              <Calendar
                                className={
                                  isSelected
                                    ? "text-white"
                                    : "text-muted-foreground"
                                }
                                size={20}
                              />
                            )}
                          </span>
                          <div className="flex min-w-0 flex-1 flex-col">
                            <span className="font-semibold">{t.name}</span>
                            {t.maxDaysPerYear != null && (
                              <span
                                className={`text-xs ${
                                  isSelected
                                    ? "text-white/80"
                                    : "text-muted-foreground"
                                }`}
                              >
                                (ສູງສຸດ {t.maxDaysPerYear} ວັນ/ປີ)
                              </span>
                            )}
                          </div>
                        </div>
                        {t.description && (
                          <p
                            className={`mt-1 line-clamp-2 text-sm ${
                              isSelected
                                ? "text-white/90"
                                : "text-muted-foreground"
                            }`}
                          >
                            {t.description}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </label>
                );
              })}
            </RadioGroup>
          )}
        />
        {methods.formState.errors.leaveTypeId && (
          <p className="mt-1 text-destructive text-sm">
            {methods.formState.errors.leaveTypeId.message}
          </p>
        )}
      </div>

      <div
        data-tourid="hr-leave-date-range"
        className="grid gap-4 sm:grid-cols-2"
      >
        <RHF.Controller
          name="startDate"
          render={({ field }) => (
            <FormInput label="ວັນທີເລີ່ມຕົ້ນ" type="date" {...field} />
          )}
        />
        <RHF.Controller
          name="endDate"
          render={({ field }) => (
            <FormInput label="ວັນທີສິ້ນສຸດ" type="date" {...field} />
          )}
        />
      </div>

      <div data-tourid="hr-leave-reason">
        <RHF.Controller
          name="reason"
          render={({ field }) => (
            <FormTextarea
              label="ເຫດຜົນ"
              placeholder="ອະທິບາຍເຫດຜົນການລາ (ຖ້າມີ)"
              rows={4}
              {...field}
            />
          )}
        />
      </div>

      <div data-tourid="hr-leave-attachment" className="space-y-2">
        <p className="mb-1 block font-medium text-sm">ເອກະສານຄັດຕິດ (ຖ້າມີ)</p>
        <p className="mb-2 text-muted-foreground text-xs">
          ລາກໄຟລ์ມາວາງ ຫຼືເລືອກໄຟລ์ — PDF, JPG ສູງສຸດ 10 MB
        </p>
        <RHF.Controller
          name="attachmentKey"
          render={({ field }) => (
            <div className="space-y-2">
              <PresignedFileDropZone
                keyPrefix={HR_LEAVE_ATTACHMENT_KEY_PREFIX}
                disabled={!!submitting}
                title="ອັບໂຫຼດໃບຮັບຮອງການລາ ຫຼືເອກະສານ"
                subtitle="PDF, JPG ສູງສຸດ 10MB"
                selectFilesLabel="SELECT FILES"
                maxBytes={10 * 1024 * 1024}
                onSuccess={(key) => field.onChange(key)}
              />
              {field.value ? (
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span
                    className="max-w-md truncate text-muted-foreground"
                    title={field.value}
                  >
                    {field.value.split("/").pop()}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => field.onChange(null)}
                  >
                    ລຶບໄຟລ์
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        />
      </div>

      {stickySubmit ? (
        <div className="sticky bottom-0 z-10 mt-8 flex flex-wrap items-center justify-between gap-4 border-t bg-background/95 py-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div className="flex flex-wrap items-center gap-4">
            {autoFillGetSamples && (
              <Button
                type="button"
                variant="outline"
                onClick={handleAutoFillSample}
                disabled={submitting}
                data-tourid="hr-leave-auto-fill-button"
              >
                {autoFillLabel}
              </Button>
            )}
            {cancelTo && (
              <Button
                type="button"
                variant="outline"
                onClick={() => window.history.back()}
              >
                ຍົກເລີກ
              </Button>
            )}
          </div>
          <div data-tourid="hr-leave-submit-button">
            <Button
              type="submit"
              disabled={submitting}
              className="bg-black text-white hover:bg-black/90"
            >
              {submitting ? "ກຳລັງສົ່ງ..." : submitLabel}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-3 pt-4">
          {autoFillGetSamples && (
            <Button
              type="button"
              variant="outline"
              onClick={handleAutoFillSample}
              disabled={submitting}
              data-tourid="hr-leave-auto-fill-button"
            >
              {autoFillLabel}
            </Button>
          )}
          {cancelTo && (
            <Button
              type="button"
              variant="outline"
              onClick={() => window.history.back()}
            >
              ຍົກເລີກ
            </Button>
          )}
          <div data-tourid="hr-leave-submit-button">
            <Button
              type="submit"
              disabled={submitting}
              className="bg-black text-white hover:bg-black/90"
            >
              {submitting ? "ກຳລັງສົ່ງ..." : submitLabel}
            </Button>
          </div>
        </div>
      )}
    </FormRoot>
  );
}
